import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  healthcareApi,
  type InterviewSchedule,
  type InterviewRecord,
  type InterviewRecordFields,
  type InterviewRecordOptionsResponse,
} from '../api/client';
import { toConsentBlock, type ConsentBlockState } from '../utils/healthcareConsent';
import {
  alreadyRecordedFrom,
  emptyRecordForm,
  fieldErrorsFrom,
  formatFollowDate,
  formatRecordedAt,
  formFromRecord,
  NEXT_FOLLOW_NEEDED,
  recordValueLabel,
} from '../utils/interviewRecords';
import InterviewRecordForm from './InterviewRecordForm';

interface Props {
  personId: string;
  options: InterviewRecordOptionsResponse;
  /** 同意が無く BE が止めたとき（画面全体を同意が必要な表示にする） */
  onConsentBlocked: (block: ConsentBlockState) => void;
}

type Editing = { mode: 'create' } | { mode: 'edit'; record: InterviewRecord } | null;

/**
 * 面談記録の一覧と入力・修正（課題 #71 / BE PR #143）。面談設定の画面（HC-S03）に置く。
 * 閲覧・作成・修正は産業保健職だけ。記録の有無や件数は管理者には出ない（BE で止める）。
 *
 * 面談予定の一覧の API が無いため、記録は面談予定に結び付けずに作る（予定なしで実施した面談の記録）。
 */
export default function InterviewRecordsSection({ personId, options, onConsentBlocked }: Props) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const items = options.items;
  const [records, setRecords] = useState<InterviewRecord[]>([]);
  /** 面談予定。null は一覧の API が無い古い BE（予定なしで記録を作る） */
  const [schedules, setSchedules] = useState<InterviewSchedule[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string[]> | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    healthcareApi
      .listInterviewRecords(personId, token)
      .then((res) => setRecords(Array.isArray(res?.data) ? res.data : []))
      .catch((err) => {
        const blocked = toConsentBlock(err);
        if (blocked) onConsentBlocked(blocked);
        else setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
      })
      .finally(() => setLoading(false));
  }, [getToken, personId, onConsentBlocked, t]);

  /** 面談予定を読み込む。一覧の API が無い古い BE（ルートが無い 404）や読めないときは、予定なしで記録を作る */
  const loadSchedules = useCallback(() => {
    const token = getToken();
    if (!token) return;
    healthcareApi
      .listInterviews(personId, token, 200)
      .then((res) => setSchedules(Array.isArray(res?.data) ? res.data : null))
      .catch((err) => {
        const blocked = toConsentBlock(err);
        if (blocked) onConsentBlocked(blocked);
        else setSchedules(null);
      });
  }, [getToken, personId, onConsentBlocked]);

  useEffect(() => {
    load();
    loadSchedules();
  }, [load, loadSchedules]);

  const open = (next: Editing) => {
    setEditing(next);
    setServerErrors(null);
    setSubmitError(null);
    setSaved(false);
  };

  const handleSubmit = async (fields: InterviewRecordFields, interviewId: string | null) => {
    const token = getToken();
    if (!token || !editing) return;
    setServerErrors(null);
    setSubmitError(null);
    try {
      if (editing.mode === 'create') {
        await healthcareApi.createInterviewRecord(
          { ...fields, user_id: personId, ...(interviewId ? { interview_id: interviewId } : {}) },
          token
        );
      } else {
        await healthcareApi.updateInterviewRecord(editing.record.id, fields, token);
      }
      setEditing(null);
      setSaved(true);
      load();
      loadSchedules();
    } catch (err) {
      const blocked = toConsentBlock(err);
      if (blocked) {
        onConsentBlocked(blocked);
        return;
      }
      // 選んだ予定に、ほかの方が先に記録していた。予定を読み込み直して選び直してもらう
      if (alreadyRecordedFrom(err)) {
        setSubmitError(t('healthcare.records.alreadyRecorded'));
        load();
        loadSchedules();
        return;
      }
      const byField = fieldErrorsFrom(err);
      if (byField) {
        setServerErrors(byField);
        return;
      }
      setSubmitError(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const label = (key: string, code: string | null | undefined) => recordValueLabel(items, key, code) || t('healthcare.records.notEntered');
  /** 記録に結び付けた面談予定の日時。一覧に無い古い予定は「予定あり」とだけ出す */
  const scheduleOf = (r: InterviewRecord) => {
    if (!r.interview_id) return t('healthcare.records.scheduleNoneShort');
    const s = schedules?.find((x) => x.id === r.interview_id);
    return s ? formatRecordedAt(s.scheduled_at) : t('healthcare.records.scheduleLinked');
  };

  return (
    <section className="healthcare-card interview-records" aria-labelledby="interview-records-heading" data-testid="interview-records">
      <h3 id="interview-records-heading">{t('healthcare.records.title')}</h3>
      <p className="healthcare-form-hint">{t('healthcare.records.intro')}</p>

      {saved && (
        <p className="interview-records-saved" role="status">
          {t('healthcare.records.saved')}
        </p>
      )}

      {error && (
        <div role="alert">
          <p className="healthcare-error-text">{error}</p>
          <button type="button" className="healthcare-btn-secondary" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {editing ? (
        <>
          <h4>{editing.mode === 'create' ? t('healthcare.records.createTitle') : t('healthcare.records.editTitle')}</h4>
          <InterviewRecordForm
            key={editing.mode === 'edit' ? editing.record.id : 'new'}
            items={items}
            initial={editing.mode === 'edit' ? formFromRecord(items, editing.record) : emptyRecordForm(items)}
            previous={editing.mode === 'edit' ? formFromRecord(items, editing.record) : undefined}
            submitLabel={editing.mode === 'create' ? t('healthcare.records.create') : t('healthcare.records.update')}
            schedules={editing.mode === 'create' ? schedules : null}
            onSubmit={handleSubmit}
            onCancel={() => open(null)}
            serverErrors={serverErrors}
            submitError={submitError}
          />
        </>
      ) : (
        <button type="button" className="healthcare-btn-primary" onClick={() => open({ mode: 'create' })}>
          {t('healthcare.records.add')}
        </button>
      )}

      {loading && <p>{t('common.loading')}</p>}
      {!loading && !error && records.length === 0 && (
        <p className="interview-records-empty">{t('healthcare.records.empty')}</p>
      )}
      {!loading && records.length > 0 && (
        <table className="healthcare-table interview-records-table">
          <thead>
            <tr>
              <th scope="col">{t('healthcare.records.column.recordedAt')}</th>
              {schedules && <th scope="col">{t('healthcare.records.column.schedule')}</th>}
              <th scope="col">{t('healthcare.records.column.format')}</th>
              <th scope="col">{t('healthcare.records.column.trigger')}</th>
              <th scope="col">{t('healthcare.records.column.plan')}</th>
              <th scope="col">{t('healthcare.records.column.nextFollow')}</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} data-testid="interview-record-row">
                <td>{formatRecordedAt(r.recorded_at)}</td>
                {schedules && <td>{scheduleOf(r)}</td>}
                <td>{label('format_code', r.format_code)}</td>
                <td>{label('trigger_code', r.trigger_code)}</td>
                <td>{label('plan_level', r.plan_level)}</td>
                <td>
                  {r.next_follow_needed === NEXT_FOLLOW_NEEDED
                    ? t('healthcare.records.nextFollowOn', { date: formatFollowDate(r.next_follow_date) })
                    : label('next_follow_needed', r.next_follow_needed)}
                </td>
                <td>
                  <button
                    type="button"
                    className="healthcare-btn-secondary"
                    onClick={() => open({ mode: 'edit', record: r })}
                    disabled={editing != null}
                  >
                    {t('healthcare.records.openEdit')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
